import type { Splide } from '@splidejs/react-splide'
import { type RefObject, useCallback, useEffect, useRef } from 'react'

type SplideInstance = NonNullable<Splide['splide']>

/** 直接 translate した後に Splide の index を実位置へ合わせる（refresh / resize 時に jump(index) されるため） */
export const syncSplideIndex = (splide: SplideInstance) => {
  const { Move, Controller } = splide.Components
  Controller.setIndex(Move.toIndex(Move.getPosition()))
}

type Params = {
  splideRef: RefObject<Splide | null>
  enabled: boolean
  direction: 'ttb' | 'rtl' | 'ltr'
  slideCount: number
}

/**
 * 表示位置より手前のスライドの大きさが変わったとき、その差分だけ位置を補正して表示を固定する。
 * Splide はブラウザのスクロールアンカリングが効かないため、画像の遅延読み込みで表示がずれるのを防ぐ。
 * 戻り値のハンドラは <Splide> の onDrag / onDragging / onDragged に渡すこと。
 */
export const useSplideScrollAnchor = ({ splideRef, enabled, direction, slideCount }: Params) => {
  const isDraggingRef = useRef(false)
  const dragOffsetRef = useRef(0)

  useEffect(() => {
    if (!enabled) return
    const splide = splideRef.current?.splide
    if (!splide) return

    const { Move, Direction, Elements, Scroll } = splide.Components
    const isVertical = direction === 'ttb'
    const sizeKey = isVertical ? 'blockSize' : 'inlineSize'
    // 監視開始前の大きさを同期的に記録する（RO の初回通知までに変わった分も差分として拾うため）
    const sizes = new Map<Element, number>(
      Elements.slides.map((slide: HTMLElement) => {
        const rect = slide.getBoundingClientRect()
        return [slide, isVertical ? rect.height : rect.width]
      }),
    )

    const observer = new ResizeObserver((entries) => {
      const changed = new Map(entries.map((entry) => [entry.target, entry.borderBoxSize[0]?.[sizeKey] ?? 0]))
      // 変更前のレイアウトで、表示領域の先頭より完全に手前にあるスライドの差分だけを足す
      const scrolled = Direction.orient(Move.getPosition())
      let start = 0
      let delta = 0
      for (const slide of Elements.slides as HTMLElement[]) {
        const prev = sizes.get(slide) ?? changed.get(slide) ?? 0
        if (start + prev > scrolled + 1) break
        delta += (changed.get(slide) ?? prev) - prev
        start += prev
      }
      changed.forEach((size, slide) => sizes.set(slide, size))
      if (Math.abs(delta) < 0.5) return

      const shift = Direction.orient(delta)
      if (isDraggingRef.current) {
        dragOffsetRef.current += shift
      } else {
        // 慣性スクロールは絶対位置へ向かうため、止めないと補正が次フレームで上書きされる（停止中なら何もしない）
        Scroll.cancel()
      }
      Move.translate(Move.getPosition() + shift)
      syncSplideIndex(splide)
    })
    Elements.slides.forEach((slide: HTMLElement) => observer.observe(slide))
    return () => observer.disconnect()
  }, [splideRef, enabled, direction, slideCount])

  const onDrag = useCallback(() => {
    isDraggingRef.current = true
    dragOffsetRef.current = 0
  }, [])

  // ドラッグ中は Splide が「押下時の位置 + 移動量」で毎回上書きするため、補正量を足し直す
  const onDragging = useCallback((splide: SplideInstance) => {
    if (dragOffsetRef.current === 0) return
    const { Move } = splide.Components
    Move.translate(Move.getPosition() + dragOffsetRef.current)
  }, [])

  const onDragged = useCallback(() => {
    isDraggingRef.current = false
    dragOffsetRef.current = 0
  }, [])

  return { onDrag, onDragging, onDragged }
}
